import React from 'react';
import { getStrategyConfig, getStrategyColors, RISK_COLORS } from '../config/strategyConfig';
import CashflowBreakdown from './CashflowBreakdown';

/**
 * StrategySelector Component
 * Displays the recommended strategy with option to view/compare all strategies
 */
function StrategySelector({ 
  recommendedStrategy, 
  allStrategies = [], 
  selectedStrategy,
  currentStrategyData,
  onStrategyChange,
  showComparison = false,
  compact = false
}) {
  // Get config for currently displayed strategy
  const displayStrategy = selectedStrategy || recommendedStrategy;
  const config = getStrategyConfig(displayStrategy);
  const colors = getStrategyColors(displayStrategy);

  // Use current (selected) strategy data for stats; fall back to recommended when nothing selected
  const recommendedData = allStrategies.find(s => s.recommended);
  const statsData = currentStrategyData || recommendedData;
  const isRecommended = displayStrategy === recommendedStrategy;

  // Get risk color
  const getRiskColor = (riskLevel) => {
    return RISK_COLORS[riskLevel] || RISK_COLORS.moderate;
  };

  const fmt = (v, f) => (v !== undefined && v !== null ? (typeof f === 'function' ? f(v) : v) : '—');

  return (
    <div className={compact ? '' : 'space-y-4'}>
      {/* Main Strategy Card - Compact or Full */}
      <div className={`${colors.bg} ${colors.border} border ${compact ? 'rounded-lg p-3' : 'rounded-xl p-5'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={compact ? 'text-2xl' : 'text-4xl'}>{config.icon}</div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className={`font-bold ${colors.text} ${compact ? 'text-base' : 'text-xl'}`}>
                  {compact ? config.shortName : config.displayName}
                </h3>
                {isRecommended && (
                  <span className={`bg-yellow-100 text-yellow-800 font-bold rounded-full ${compact ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-xs'}`}>
                    ★ REC
                  </span>
                )}
              </div>
              {!compact && (
                <p className="text-sm text-gray-600 mt-1 max-w-md">
                  {config.description}
                </p>
              )}
            </div>
          </div>
          
          {/* Strategy Score Badge (from analysis) */}
          {statsData != null && (
            <div className="text-right flex-shrink-0">
              <div className={`font-bold ${colors.text} ${compact ? 'text-xl' : 'text-3xl'}`}>
                {fmt(statsData.score)}/100
              </div>
              {!compact && <div className="text-xs text-gray-500">Strategy Score</div>}
            </div>
          )}
        </div>

        {/* Quick Stats Row (from analysis for selected strategy) */}
        {statsData != null && (
          <div className={`border-t border-gray-200/50 ${compact ? 'mt-2 pt-2' : 'mt-4 pt-4'}`}>
            <div className={`flex items-center gap-4 ${compact ? 'text-xs' : 'text-sm'} flex-wrap`}>
              <div className="flex items-center gap-1 group relative">
                <span className="text-gray-500 border-b border-dotted border-gray-400 cursor-help" title="Annual pre-tax cashflow ÷ cash to complete (deposit + acquisition costs modelled). Interest-only mortgage.">
                  Cash-on-cash (Y1):
                </span>
                <span className="font-bold text-gray-900">
                  {fmt(statsData.estimated_roi_percent, (v) => `${Number(v).toFixed(1)}%`)}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Time:</span>
                <span className="font-bold text-gray-900">
                  {statsData.timeline_months != null ? `${statsData.timeline_months}m` : '—'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Risk:</span>
                {statsData.risk_level ? (
                  <span className={`rounded font-semibold ${compact ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-xs'} ${getRiskColor(statsData.risk_level).bg} ${getRiskColor(statsData.risk_level).text}`}>
                    {getRiskColor(statsData.risk_level).label}
                  </span>
                ) : (
                  <span className="text-gray-500">—</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500 border-b border-dotted border-gray-400 cursor-help" title="Cash to complete = deposit + SDLT + legal + survey + furnishing (+ conversion/licensing for HMO). Matches cash-on-cash divisor.">
                  Cash to complete:
                </span>
                <span className="font-bold text-gray-900">
                  {statsData.initial_capital_required != null ? `£${(statsData.initial_capital_required / 1000).toFixed(0)}k` : '—'}
                </span>
              </div>
            </div>
            <CashflowBreakdown breakdown={statsData.financial_breakdown} compact={compact} />
          </div>
        )}
      </div>

      {/* Strategy Comparison Toggle */}
      {showComparison && allStrategies.length > 1 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">
            Compare All Strategies
          </div>
          <div className="flex flex-wrap gap-2">
            {allStrategies.map((strategy) => {
              const sConfig = getStrategyConfig(strategy.strategy);
              const sColors = getStrategyColors(strategy.strategy);
              const isSelected = displayStrategy === strategy.strategy;
              const isRec = strategy.recommended;

              return (
                <button
                  key={strategy.strategy}
                  onClick={() => onStrategyChange?.(strategy.strategy)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                    isSelected 
                      ? `${sColors.bg} ${sColors.border} ring-2 ring-offset-1 ring-gray-300` 
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <span>{sConfig.icon}</span>
                  <div className="text-left">
                    <div className={`text-sm font-semibold ${isSelected ? sColors.text : 'text-gray-700'}`}>
                      {sConfig.shortName}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold text-gray-900">
                        {strategy.score}
                      </span>
                      {isRec && (
                        <span className="text-xs">★</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default StrategySelector;
